import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, UserCheck } from "lucide-react";

/**
 * Admin-only dialog: manually connect a user (by email) to this business.
 * Bypasses the buyer/claim flow entirely.
 *
 * Two-step flow when a conflict is returned (409 owner_conflict):
 *   1. Server responds with the current owner's details.
 *   2. Dialog shows a confirmation banner + "Override anyway" button that
 *      retries with force=true.
 */
export default function AssignOwnerDialog({ open, onOpenChange, business, onAssigned }) {
  const [email, setEmail] = useState("");
  const [markVerified, setMarkVerified] = useState(true);
  const [markClaimed, setMarkClaimed] = useState(true);
  const [notifyUser, setNotifyUser] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState(null); // { current_owner, message }

  const reset = () => {
    setEmail("");
    setMarkVerified(true);
    setMarkClaimed(true);
    setNotifyUser(true);
    setConflict(null);
    setSubmitting(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const submit = async (force) => {
    if (!business || !email.trim()) {
      toast.error("Enter a user email to assign.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/admin/businesses/${business.business_id}/assign-owner`,
        {
          email: email.trim(),
          force: !!force,
          mark_verified: markVerified,
          mark_claimed: markClaimed,
          notify_user: notifyUser,
        },
        {
          withCredentials: true,
        },
      );
      const assigned = res.data?.assigned_to || {};
      toast.success(
        `${assigned.email || "User"} is now the owner of ${business.name}.`,
      );
      onAssigned?.(res.data?.business);
      close();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "owner_conflict") {
        setConflict(detail);
      } else {
        const msg = typeof detail === "string" ? detail : detail?.message || "Failed to assign owner";
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!business) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg" data-testid="assign-owner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-tbc-navy">
            <UserCheck className="w-5 h-5 text-tbc-gold" />
            Assign owner to {business.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="assign-owner-email" className="text-sm font-medium">
              User email
            </Label>
            <Input
              id="assign-owner-email"
              type="email"
              autoComplete="off"
              placeholder="owner@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (conflict) setConflict(null);
              }}
              disabled={submitting}
              data-testid="assign-owner-email-input"
              className="mt-1"
            />
            <p className="mt-1 text-xs text-gray-500">
              The user must already have a Blk Community account (Google or email/password). Existing user type
              is irrelevant — buyers get promoted to business owner automatically.
            </p>
          </div>

          <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={markClaimed}
                onCheckedChange={(v) => setMarkClaimed(!!v)}
                disabled={submitting}
                data-testid="assign-owner-mark-claimed"
              />
              <span>Mark business as <strong>claimed</strong></span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={markVerified}
                onCheckedChange={(v) => setMarkVerified(!!v)}
                disabled={submitting}
                data-testid="assign-owner-mark-verified"
              />
              <span>Mark business as <strong>verified</strong> (gold border)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={notifyUser}
                onCheckedChange={(v) => setNotifyUser(!!v)}
                disabled={submitting}
                data-testid="assign-owner-notify-user"
              />
              <span>Email + in-app notify the new owner</span>
            </label>
          </div>

          {conflict && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="assign-owner-conflict-banner"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" />
                <div className="space-y-1">
                  <p className="font-semibold">This business already has an owner.</p>
                  <p>
                    Current owner:{" "}
                    <span className="font-medium">
                      {conflict.current_owner?.name || conflict.current_owner?.email || "Unknown"}
                    </span>
                    {conflict.current_owner?.email && conflict.current_owner?.name && (
                      <> ({conflict.current_owner.email})</>
                    )}
                  </p>
                  <p className="text-xs">
                    Overriding will replace them silently. The previous owner will lose dashboard access to this business.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={close}
            disabled={submitting}
            data-testid="assign-owner-cancel-btn"
          >
            Cancel
          </Button>
          {conflict ? (
            <Button
              type="button"
              onClick={() => submit(true)}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="assign-owner-override-btn"
            >
              {submitting ? "Overriding…" : "Override anyway"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => submit(false)}
              disabled={submitting || !email.trim()}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
              data-testid="assign-owner-submit-btn"
            >
              {submitting ? "Assigning…" : "Assign owner"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
